// Lib
import React from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash/fp';
import classNames from 'classnames';
import { connect, useSelector } from 'react-redux';

// Utils
import { isPlatformIframe, isPlatformLegacyMobileApp } from '../../../platform/utils/platformDetailsUtils';

// Selectors
import { getPlatformDetailsSelector } from '../../../platform/platformSelector';

// Components
import RegisterForm from './RegisterForm';
import FullScreenModal from '../../../components/modal/FullScreenModal';
import RouteLink from '../../../components/buttons/RouteLink';
import Icon from '../../../components/icons/Icon';
import LoadableLogin from '../login/LoadableLogin';
import AuthFormDisclaimerSection from '../../AuthFormDisclaimerSection';
import GoogleLogo from '../../icons/GoogleLogo';
import AppleLogo from '../../icons/AppleLogo';
import SocialSignInButton from '../../SocialSignInButton';
import { MobileAndTabletComponentSwitch } from '../../../mobile/utils/MobileAndTabletComponentSwitch';
import { AuthSheet } from '../splash/AuthSheet';

// Actions
import { navigateToRegisterTerms } from '../../../reducers/navigationActions';
import { logout } from '../../authActions';

// Constants
import { USER_SOCIAL_AUTH_VENDORS } from '../../../../common/users/userConstants';

// Styles
import '../AuthModal.scss';

const showOverlay = (props) => get(['route', 'overlay'], props) || false;
const getLoginPath = (props) => get(['route', 'loginPath'], props) || 'login';

const mapDispatchToProps = (dispatch, { location }) => ({
    dispatchNavigateToRegisterTerms: (vendor) =>
        dispatch(
            navigateToRegisterTerms({
                // Pass origin, to allow Google to redirect back to this page on an unsuccessful authentication
                origin: encodeURIComponent(window.location.href),

                // Pass existing query params
                ...location?.query,

                // Pass in social sign in vendor, to allow register-terms page to know how to handle social sign up
                with: vendor,
            }),
        ),
    dispatchForceLogout: () => dispatch(logout()),
});

const Container = MobileAndTabletComponentSwitch(FullScreenModal, AuthSheet);

const Register = (props) => {
    const { dispatchNavigateToRegisterTerms, dispatchForceLogout } = props;

    const platformDetails = useSelector(getPlatformDetailsSelector);

    if (isPlatformIframe(platformDetails)) return null;

    React.useEffect(() => {
        LoadableLogin.preload();

        // on iPad hybrid app, always defer to native login, so force a logout if Register is mounted
        if (isPlatformLegacyMobileApp(platformDetails)) dispatchForceLogout();
    }, []);

    return (
        <Container
            className={classNames('AuthModal Register', props.className)}
            overlay={showOverlay(props)}
            close={props.close}
        >
            {props.children || (
                <>
                    <div className="logo">
                        <Icon name="milanote-logo-medium" />
                    </div>

                    <div className="heading">
                        <h1>Sign up to Milanote</h1>
                    </div>
                </>
            )}

            <div className="section">
                <RegisterForm {...props} />
            </div>

            <div className="section">
                <div className="social-buttons">
                    <SocialSignInButton
                        logo={<GoogleLogo />}
                        onClick={() => dispatchNavigateToRegisterTerms(USER_SOCIAL_AUTH_VENDORS.GOOGLE)}
                    >
                        Sign up with Google
                    </SocialSignInButton>
                    <SocialSignInButton
                        logo={<AppleLogo />}
                        onClick={() => dispatchNavigateToRegisterTerms(USER_SOCIAL_AUTH_VENDORS.APPLE)}
                    >
                        Sign up with Apple
                    </SocialSignInButton>
                </div>

                <div className="register-instead">
                    Already have an account?{' '}
                    <RouteLink to={`/${getLoginPath(props)}`} keepParams location={window.location}>
                        Log in
                    </RouteLink>
                </div>
            </div>

            <AuthFormDisclaimerSection />
        </Container>
    );
};

Register.propTypes = {
    children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
    className: PropTypes.string,
    dispatchNavigateToRegisterTerms: PropTypes.func,
    dispatchForceLogout: PropTypes.func,
    close: PropTypes.func,
};

export default connect(null, mapDispatchToProps)(Register);
